import { type Instructor } from "@core/instructors/types";
import { BaseLink } from "@design-system/components/link";
import { trans } from "@mongez/localization";
import { User } from "lucide-react";

interface InstructorsSectionProps {
  instructors: Instructor[];
}

export function InstructorsSection({ instructors }: InstructorsSectionProps) {
  if (!instructors || instructors.length === 0) return null;

  return (
    <section className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 sm:text-4xl">
          {trans("home.meetInstructors")}
        </h2>
        <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
          {trans("home.instructorsDescription")}
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-6">
        {instructors.map((instructor) => (
          <div
            key={instructor.id}
            className="group relative w-full max-w-sm overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg dark:border-gray-700 dark:bg-gray-800 sm:w-80"
          >
            {/* Background gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 to-red-50/50 opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-orange-950/30 dark:to-red-950/30" />

            <div className="relative z-10">
              {/* Instructor Image */}
              <div className="mb-4 flex justify-center">
                <div className="relative">
                  <div className="h-20 w-20 overflow-hidden rounded-full bg-gradient-to-br from-orange-500 to-red-600 p-1 shadow-lg">
                    {instructor.image ? (
                      <img
                        src={instructor.image.url}
                        alt={instructor.name}
                        className="h-full w-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center rounded-full bg-white dark:bg-gray-700">
                        <User className="h-10 w-10 text-orange-600" />
                      </div>
                    )}
                  </div>
                  {/* Online status indicator */}
                  <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white bg-green-500" />
                </div>
              </div>

              {/* Instructor Info */}
              <div className="space-y-3 text-center">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 transition-colors group-hover:text-orange-600 dark:text-gray-100 dark:group-hover:text-orange-400">
                    {instructor.name}
                  </h3>
                  {instructor.label && (
                    <p className="text-sm font-medium text-orange-600">
                      {instructor.label}
                    </p>
                  )}
                </div>

                {/* Action Button */}
                <BaseLink
                  to={`/instructors/${instructor.id}`}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-orange-500 to-red-600 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:from-orange-600 hover:to-red-700 hover:shadow-lg"
                >
                  {trans("home.viewProfile")}
                </BaseLink>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -right-2 -top-2 h-8 w-8 rounded-full bg-orange-100 opacity-0 transition-all duration-300 group-hover:opacity-100 dark:bg-orange-900" />
            <div className="absolute -bottom-2 -left-2 h-6 w-6 rounded-full bg-red-100 opacity-0 transition-all duration-300 group-hover:opacity-100 dark:bg-red-900" />
          </div>
        ))}
      </div>
    </section>
  );
}
